import { ApiError } from './client'

const logApiErrorForDev = (error: ApiError): void => {
  if (import.meta.env.DEV) {
    console.error(`[api] HTTP ${error.status}`, error.message)
  }
}

/** Понятные тексты для машинных кодов ошибок API (когда сервер не прислал message). */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  internal_error: 'Внутренняя ошибка сервера. Попробуйте позже или обратитесь в поддержку.',
  invalid_request: 'Некорректный запрос. Проверьте данные и попробуйте снова.',
  validation_failed: 'Проверьте корректность заполнения полей.',
  not_found: 'Запрашиваемые данные не найдены.',
  unauthorized: 'Требуется вход в систему.',
  forbidden: 'Недостаточно прав для этого действия.',
  invalid_credentials: 'Неверный email или пароль.',
  invalid_session: 'Сессия истекла. Войдите снова.',
}

type ApiErrorBody = {
  error?: string
  message?: string
}

/** Разбирает JSON-тело ошибки API; для не-JSON (plain text, HTML) возвращает null. */
const parseApiErrorBody = (raw: string): ApiErrorBody | null => {
  if (!raw.startsWith('{')) {
    return null
  }
  try {
    return JSON.parse(raw) as ApiErrorBody
  } catch {
    return null
  }
}

export const formatApiErrorMessage = (error: unknown, fallback = 'Не удалось выполнить запрос'): string => {
  if (error instanceof ApiError) {
    logApiErrorForDev(error)

    if (error.status === 401) {
      return 'Неверный email или пароль'
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return 'Сервис временно недоступен. Попробуйте позже.'
    }

    const raw = error.message.trim()
    if (raw.toLowerCase().includes('bad gateway')) {
      return 'Сервис временно недоступен. Попробуйте позже.'
    }

    // JSON-тело: не показываем сырой JSON — берём message, затем понятный текст по коду.
    const body = parseApiErrorBody(raw)
    if (body) {
      if (body.message?.trim()) {
        return body.message.trim()
      }
      if (body.error && ERROR_CODE_MESSAGES[body.error]) {
        return ERROR_CODE_MESSAGES[body.error]
      }
      return fallback
    }

    if (raw && !raw.startsWith('<')) {
      return raw
    }
    return fallback
  }

  if (error instanceof TypeError) {
    if (import.meta.env.DEV) {
      console.error('[api] network error', error)
    }
    return 'Нет связи с сервером. Проверьте интернет и попробуйте снова.'
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}
